import { type CSSProperties, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import AppearanceMenu from './AppearanceMenu.js';
import './conductor.css';
import { CONDUCTOR_THEMES } from './theme.js';
import type { Appearance } from './types.js';
import { useAppearance } from './useAppearance.js';

type NavKey = 'dashboard' | 'board' | 'activity' | 'security' | 'engineering' | 'settings';

const NAV_ITEMS: { key: NavKey; label: string; to: string }[] = [
  { key: 'dashboard', label: 'Dashboard', to: '/dashboard' },
  { key: 'board', label: 'Board', to: '/board' },
  { key: 'activity', label: 'Activity', to: '/activity' },
  { key: 'security', label: 'Security', to: '/security' },
  { key: 'engineering', label: 'Engineering', to: '/engineering' },
  { key: 'settings', label: 'Settings', to: '/settings' },
];

async function logout() {
  await fetch('/api/auth/logout', { method: 'POST' });
  window.location.href = '/login';
}

export default function ThemedShell({
  activeNav,
  toolbarExtra,
  children,
}: {
  activeNav: NavKey | null;
  toolbarExtra?: ReactNode;
  children: ReactNode | ((appearance: Appearance) => ReactNode);
}) {
  const [appearance, setAppearance] = useAppearance();
  const tokens = CONDUCTOR_THEMES[appearance.direction].tokens;

  const rootStyle = {
    ...tokens,
    background: 'var(--bg)',
    color: 'var(--text)',
    minHeight: '100vh',
    width: '100%',
    transition: 'background .25s',
  } as unknown as CSSProperties;

  return (
    <div data-theme={appearance.direction} data-density={appearance.density} style={rootStyle}>
      <div className="conductor-dashboard">
        <div className="conductor-header">
          <nav className="conductor-nav">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.key}
                to={item.to}
                className={`conductor-nav-link${item.key === activeNav ? ' conductor-nav-link-active' : ''}`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
          <div className="conductor-header-right">
            {toolbarExtra}
            <Link to="/drop-idea" className="conductor-toolbar-btn">
              + Drop Idea
            </Link>
            <Link to="/new" className="conductor-toolbar-btn">
              + New Spec
            </Link>
            <AppearanceMenu appearance={appearance} setAppearance={setAppearance} />
            <button type="button" className="conductor-toolbar-btn" style={{ fontFamily: 'inherit' }} onClick={logout}>
              Log out
            </button>
          </div>
        </div>

        {typeof children === 'function' ? children(appearance) : children}
      </div>
    </div>
  );
}
